import { useState } from "react";
import type { FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { AuthSession, DriverSignup } from "../../types/vtc";
import { AUTH_CONTENT, safeNext } from "../../data/auth";
import { registerDriver, saveSession } from "../../lib/auth";
import { ApiError, errorText, focusFirstError } from "../../lib/api";
import { toast } from "../../lib/toast";
import type { AddressParts } from "../../lib/googlePlaces";
import AddressField from "../ui/AddressField";
import Button from "../ui/Button";
import { FormAlert, PasswordField, TextField } from "../ui/FormField";
import EmailCodeForm from "./EmailCodeForm";
import Turnstile from "../ui/Turnstile";
import { turnstileEnabled } from "../../lib/turnstile";
import StepProgress from "./StepProgress";
import TermsCheckbox from "./TermsCheckbox";
import { emailRules, newPasswordRules, phoneRules, required } from "./rules";
import { useT } from "../../i18n";

/** Fields validated before leaving each step: Account, Identity, Licence and VTC. */
const STEP_FIELDS: (keyof DriverSignup)[][] = [
  ["email", "phone", "password", "acceptTerms"],
  ["firstName", "lastName", "address", "postalCode", "city", "country"],
  ["licenceNumber", "licenceDate", "vtcCardNumber", "vtcCardExpiry"],
];
const LAST_FORM_STEP = STEP_FIELDS.length - 1;
/** After the form: the e-mailed code. */
const VERIFY_STEP = STEP_FIELDS.length;

const today = () => new Date().toISOString().slice(0, 10);

export default function DriverSignupForm() {
  const t = useT();
  const f = t.auth.driverSignup;
  // Address labels are shared with the company forms.
  const c = t.auth.companySignup;
  const REQUIRED = t.common.required;
  const notBlank = (v: string) => v.trim() !== "" || REQUIRED;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DriverSignup>({
    defaultValues: {
      email: "",
      phone: "",
      password: "",
      acceptTerms: false,
      firstName: "",
      lastName: "",
      address: "",
      postalCode: "",
      city: "",
      state: "",
      country: "",
      licenceNumber: "",
      licenceDate: "",
      vtcCardNumber: "",
      vtcCardExpiry: "",
    },
  });

  /** Fills postcode, city, region and country from the picked Google suggestion. */
  const fillAddress = (parts: AddressParts) => {
    const fields = {
      address: parts.street,
      city: parts.city,
      state: parts.state,
      postalCode: parts.postalCode,
      country: parts.country,
    } satisfies Partial<DriverSignup>;
    for (const [name, value] of Object.entries(fields)) {
      // Keep what the user typed when Google has no street-level detail.
      if (name === "address" && !value) continue;
      setValue(name as keyof typeof fields, value, { shouldValidate: true, shouldDirty: true });
    }
  };

  const goTo = (next: number) => {
    // A failed submit's message belongs to that attempt, not to the step shown next.
    setSubmitError(null);
    setStep(next);
    window.scrollTo({ top: 0 });
  };

  // Cloudflare Turnstile (bot check), checked when the account is created; one token per attempt
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);

  /** "Create my account": the backend e-mails a code, then we ask for it. */
  const createAccount = handleSubmit(async (values) => {
    setSubmitError(null);
    if (turnstileEnabled && !captcha) return setSubmitError(t.captcha.required);
    try {
      const { email: sentTo } = await registerDriver(values, captcha);
      setEmail(sentTo);
      toast.success(f.codeSentTo(sentTo));
      goTo(VERIFY_STEP);
    } catch (err) {
      // Field errors (e.g. e-mail already used) go back to the step that has the field.
      if (err instanceof ApiError && err.errors) {
        const fields = Object.keys(err.errors) as (keyof DriverSignup)[];
        const back = STEP_FIELDS.findIndex((list) => list.some((k) => fields.includes(k)));
        fields.forEach((k) => setError(k, { message: err.errors![k] }));
        if (back >= 0 && back !== step) goTo(back);
        focusFirstError();
      }
      const message = errorText(err);
      setSubmitError(message);
      toast.error(message);
      setCaptchaReset((n) => n + 1); // the token was used: get a new one
    }
  });

  const onSubmit = async (e: FormEvent) => {
    if (step === LAST_FORM_STEP) return createAccount(e);
    e.preventDefault();
    if (await trigger(STEP_FIELDS[step])) goTo(step + 1);
    else focusFirstError();
  };

  const onVerified = (session: AuthSession, message: string) => {
    saveSession(session, true);
    toast.success(message);
    navigate(safeNext(searchParams.get("next")) ?? AUTH_CONTENT.driver.dashboardHref);
  };

  if (step === VERIFY_STEP) {
    return (
      <>
        <StepProgress steps={f.steps} current={VERIFY_STEP} />
        <EmailCodeForm role="driver" email={email} onVerified={onVerified} />
      </>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <StepProgress steps={f.steps} current={step} />

      {step === 0 && (
        <div className="grid gap-3.5 sm:grid-cols-2">
          <TextField
            full
            label={c.emailAddress}
            type="email"
            autoComplete="email"
            placeholder={f.emailPlaceholder}
            error={errors.email?.message}
            {...register("email", emailRules(t))}
          />
          <TextField
            label={f.mobile}
            type="tel"
            autoComplete="tel"
            placeholder="06 12 34 56 78"
            error={errors.phone?.message}
            {...register("phone", phoneRules(t))}
          />
          <PasswordField
            label={t.common.password}
            autoComplete="new-password"
            placeholder={c.passwordPlaceholder}
            error={errors.password?.message}
            {...register("password", newPasswordRules(t))}
          />
          <TermsCheckbox
            className="sm:col-span-2"
            error={errors.acceptTerms?.message}
            registration={register("acceptTerms", { required: t.auth.terms.required })}
          />
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-3.5 sm:grid-cols-2">
          <TextField
            label={f.firstName}
            placeholder={t.common.ph.firstName}
            autoComplete="given-name"
            error={errors.firstName?.message}
            {...register("firstName", required(f.firstNameRequired))}
          />
          <TextField
            label={f.lastName}
            placeholder={t.common.ph.lastName}
            autoComplete="family-name"
            error={errors.lastName?.message}
            {...register("lastName", required(f.lastNameRequired))}
          />
          <AddressField
            full
            label={c.address}
            placeholder={f.addressPlaceholder}
            autoComplete="street-address"
            error={errors.address?.message}
            onAddressSelect={fillAddress}
            {...register("address", { required: REQUIRED, validate: notBlank })}
          />
          <TextField
            label={f.postcode}
            placeholder={t.common.ph.postalCode}
            autoComplete="postal-code"
            error={errors.postalCode?.message}
            {...register("postalCode", { required: REQUIRED, validate: notBlank })}
          />
          <TextField
            label={c.city}
            placeholder={t.common.ph.city}
            autoComplete="address-level2"
            error={errors.city?.message}
            {...register("city", { required: REQUIRED, validate: notBlank })}
          />
          <TextField
            label={c.state}
            placeholder={t.common.ph.region}
            autoComplete="address-level1"
            error={errors.state?.message}
            {...register("state")}
          />
          <TextField
            label={c.country}
            placeholder={t.common.ph.country}
            autoComplete="country-name"
            error={errors.country?.message}
            {...register("country", { required: REQUIRED, validate: notBlank })}
          />
        </div>
      )}

      {step === 2 && (
        <div className="grid gap-3.5 sm:grid-cols-2">
          <TextField
            label={f.licenceNumber}
            placeholder={t.common.ph.licenceNumber}
            error={errors.licenceNumber?.message}
            {...register("licenceNumber", { required: REQUIRED, validate: notBlank })}
          />
          <TextField
            label={f.licenceDate}
            type="date"
            max={today()}
            error={errors.licenceDate?.message}
            {...register("licenceDate", {
              required: f.licenceDateRequired,
              validate: (v) => v <= today() || f.licenceDateFuture,
            })}
          />
          <TextField
            label={f.vtcCard}
            placeholder={f.vtcCardNumberPlaceholder}
            error={errors.vtcCardNumber?.message}
            {...register("vtcCardNumber", required(f.vtcCardRequired))}
          />
          <TextField
            label={f.vtcCardExpiry}
            type="date"
            min={today()}
            error={errors.vtcCardExpiry?.message}
            {...register("vtcCardExpiry", {
              required: REQUIRED,
              validate: (v) => v >= today() || f.vtcCardExpired,
            })}
          />
        </div>
      )}

      {/* Shown on every step, like the company form; it stays mounted so Cloudflare
          refreshes the check if the driver takes long to fill the 3 steps */}
      <Turnstile onToken={setCaptcha} resetKey={captchaReset} className="mt-5" />

      {submitError && (
        <div className="mt-4">
          <FormAlert>{submitError}</FormAlert>
        </div>
      )}

      <div className="mt-7 flex gap-2.5">
        {step > 0 && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => goTo(step - 1)}
            disabled={isSubmitting}
            className="h-11 cursor-pointer border-0"
          >
            {t.common.back}
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting} className="h-11 flex-1 cursor-pointer border-0">
          {step < LAST_FORM_STEP ? f.continue : isSubmitting ? f.creating : f.createAccount}
        </Button>
      </div>
    </form>
  );
}
